import { Analytics } from '@easy-expense/analytics-client';
import {
  cancelPaddleSubscriptionFunction,
  resumePaddleSubscriptionFunction,
  currentUser,
  getPaddleBillingFunction,
  getPaddleSubscriptionBillingInfoFunction,
  getWorkspaceSubscriptionStatusFunction,
  previewPaddleSubscriptionChangeFunction,
  upgradePaddleSubscriptionFunction,
} from '@easy-expense/auth-client';
import {
  useCachedMembers,
  useCurrentOrganization,
  useCurrentWorkspace,
} from '@easy-expense/data-firestore-client';
import {
  PaddleBillingUsage,
  SubscriptionChargePreview,
  WorkspaceSubscriptionStatus,
} from '@easy-expense/data-schema-v2';
import { WebEnv } from '@easy-expense/env-mobile';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import {
  buildTeamCheckoutQuery,
  SubscriptionBilling,
  TEAM_CHECKOUT_PATH,
} from '@easy-expense/utils-shared';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BillingTab } from './BillingTab.component';
import { ConfirmationModal } from './ConfirmationModal.component';
import { PlanGrid } from './PlanGrid.component';
import { PlatformSubscriptionNote } from './PlatformSubscriptionNote.component';
import { SeatSelectionModal } from './SeatSelectionModal.component';
import { StatStrip } from './StatStrip.component';
import { SubscriptionTab } from './SubscriptionTab.component';
import { TermToggle } from './TermToggle.component';
import { TrustStrip } from './TrustStrip.component';
import { computeHasNoPaddleInvolvement } from './computeHasNoPaddleInvolvement';
import { computeIsCoveredByTeammate } from './computeIsCoveredByTeammate';
import { notifyBillingChanged } from '../../../lib/Billing/billingChanged';
import {
  ADD_SEATS_PARAM,
  CHECKOUT_COMPLETE_PARAM,
  CHECKOUT_COMPLETE_VALUE,
  LEGACY_TEAMS_PRO_MONTHLY_PRICE,
  MONTHLY_PRICES,
  TEAM_MIN_SEATS,
} from '../../../lib/Billing/constants';
import {
  buildIndividualCheckoutQuery,
  INDIVIDUAL_CHECKOUT_PATH,
} from '../../../lib/Billing/individualCheckout';
import { computeIsOverSeats } from '../../../lib/Billing/seatsOverage';
import {
  BillingTerm,
  getTeamPlanTitle,
  individualPlanFromPriceID,
  IndividualPlanCode,
  paddleIndividualItemsProd,
  paddleIndividualItemsSandbox,
  paddleTeamItemsNewAnnualProd,
  paddleTeamItemsNewAnnualSandbox,
  paddleTeamItemsNewMonthlyProd,
  paddleTeamItemsNewMonthlySandbox,
  priceIsAnnual,
  teamPlanFromPriceID,
  TeamPlanCode,
} from '../../../lib/Paddle';
import { Button } from '../../Button.components';
import LoadingSpinner from '../../LoadingSpinner.component';

type ActiveTab = 'subscription' | 'billing';

type ConfirmModalProps = {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  dismissLabel?: string;
  destructive?: boolean;
  getPreview?: () => Promise<SubscriptionChargePreview>;
};

const MAX_PAYMENT_DETAIL_RETRIES = 5;

/**
 * Whether two Paddle price IDs represent the same plan tier, ignoring the
 * trial/no-trial variant and billing cycle. Used to tell a quantity-only seat
 * change apart from a genuine plan switch.
 */
function samePlanTier(a: string, b: string): boolean {
  const teamA = teamPlanFromPriceID(a);
  const teamB = teamPlanFromPriceID(b);
  if (teamA !== null && teamB !== null) {
    return teamA === teamB;
  }
  const individualA = individualPlanFromPriceID(a);
  const individualB = individualPlanFromPriceID(b);
  if (individualA !== null && individualB !== null) {
    return individualA === individualB;
  }
  return false;
}

// Subscription-agnostic display values for a read-only view whose coverage isn't reflected by
// billingUsage (Paddle-only) — a teammate's subscription, or the viewer's own RevenueCat one.
function resolveWorkspaceStatusDisplay(status: WorkspaceSubscriptionStatus | null): {
  planLabel: string;
  nextBillingLabel: string;
  perPeriodLabel: string;
  perPeriodAmount: string;
} {
  // The backend's tier is a coarse 'pro' | 'starter' — a Team plan and an individual Professional
  // plan both report 'pro'. No price ID reaches this codepath (unlike the owner's own view, which
  // reads it off billingUsage), so seats is the only signal available to tell them apart: a
  // genuine Team purchase always buys at least TEAM_MIN_SEATS seats, while an individual
  // Professional plan always buys exactly 1.
  let planLabel = getTranslation('Starter');
  if (status?.tier === 'pro') {
    planLabel =
      (status.seatsAllowed ?? 0) >= TEAM_MIN_SEATS
        ? getTranslation('Team')
        : getTranslation('Professional');
  }
  const nextBillingLabel = status?.expiresAt
    ? new Date(status.expiresAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';
  const perPeriodLabel =
    status?.billingFrequency === 'year' ? getTranslation('Per year') : getTranslation('Per month');
  // While trialing, formattedPrice is meaningless (RevenueCat has no field anywhere that reports
  // a trialing product's eventual regular price -- see isTrialing's own doc comment), so show a
  // trial-appropriate label instead of a stale/blank price. Already formatted server-side
  // otherwise (see resolveSubscriptionBillingDetails) -- Paddle and RevenueCat report price on
  // different scales, so this never parses/reformats it itself.
  const perPeriodAmount = status?.isTrialing
    ? getTranslation('Free trial')
    : (status?.formattedPrice ?? '—');
  return { planLabel, nextBillingLabel, perPeriodLabel, perPeriodAmount };
}

function paymentsHistoryURL(overviewUrl: string | undefined): string | undefined {
  if (!overviewUrl) {
    return undefined;
  }
  try {
    const url = new URL(overviewUrl);
    if (!url.pathname.startsWith('/payments')) {
      url.pathname = `/payments${url.pathname}`;
    }
    return url.toString();
  } catch {
    return overviewUrl;
  }
}

const TabButton: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: number;
}> = ({ label, active, onClick, badge }) => {
  return (
    <Layout.PressableRow
      align
      py={12}
      px={4}
      bg="white"
      onClick={onClick}
      style={{
        marginRight: 24,
        borderBottom: `2px solid ${active ? theme.colors.brandPrimary : 'transparent'}`,
      }}
    >
      <OpenSans.Primary
        size="s-16"
        weight={active ? 'bold-700' : 'regular-400'}
        color={active ? 'brandPrimary' : 'secondary'}
      >
        {label}
      </OpenSans.Primary>
      {badge && badge > 0 ? (
        <>
          <Spacer.Horizontal size={8} />
          <Layout.Row
            center
            radius={100}
            px={6}
            bg={active ? 'destructive' : 'destructiveXLight'}
            style={{ minWidth: 18, height: 18 }}
          >
            <OpenSans.Primary
              size="xs-12"
              weight="bold-700"
              color={active ? 'inverse' : 'destructive'}
            >
              {`${badge} ${getTranslation('over')}`}
            </OpenSans.Primary>
          </Layout.Row>
        </>
      ) : null}
    </Layout.PressableRow>
  );
};

export const BillingContent: React.FC = () => {
  const [billingInfo, setBillingInfo] = React.useState<SubscriptionBilling | null>(null);
  const [billingUsage, setBillingUsage] = React.useState<PaddleBillingUsage | null>(null);
  const [billingInfoError, setBillingInfoError] = React.useState(false);
  const [billingUsageError, setBillingUsageError] = React.useState(false);
  const [workspaceSubscriptionStatus, setWorkspaceSubscriptionStatus] =
    React.useState<WorkspaceSubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [term, setTerm] = React.useState<BillingTerm>('monthly');
  const [pendingPlan, setPendingPlan] = React.useState<TeamPlanCode | null>(null);
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('subscription');
  const [busyLabel, setBusyLabel] = React.useState<string | null>(null);
  const [confirmModalProps, setConfirmModalProps] = React.useState<ConfirmModalProps | null>(null);
  // Scroll target for an existing Team subscriber's "Change plan" row — mirrors dev's
  // planCardRef scroll-to-upgrade-card pattern.
  const planGridRef = React.useRef<HTMLDivElement | null>(null);

  const currentWorkspace = useCurrentWorkspace();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentOrganization = useCurrentOrganization();
  const members = useCachedMembers();

  const currentPlan = teamPlanFromPriceID(billingUsage?.activePriceID);
  const currentAllocation = billingUsage?.purchasedSeats ?? currentOrganization?.seats ?? 0;
  // billingUsage.activeSeats is the server's authoritative count, but it
  // lags client-side membership changes by a webhook round-trip. Take the
  // max of the two so we never under-report overage while a recent invite
  // is still propagating; the server number wins again once it catches up.
  // TODO: collapse to a single server-returned value (https://linear.app/easy-expense)
  const activeMembers = Math.max(billingUsage?.activeSeats ?? 0, members?.length ?? 0);
  // currentAllocation is already the real seat cap for whatever plan is active — 0 for no
  // subscription, the bundled entitlement for an individual plan (Starter/Professional), or the
  // purchased quantity for Team — so every plan participates in the same overage check.
  const isOverSeats = computeIsOverSeats({
    hasLoaded: billingUsage !== null,
    usedSeats: activeMembers,
    allowedSeats: currentAllocation,
  });
  const overSeatsCount = Math.max(activeMembers - currentAllocation, 0);
  const isOnAnnualSubscription = billingUsage?.frequency === 'year';

  React.useEffect(() => {
    if (isOnAnnualSubscription && term !== 'annual') {
      setTerm('annual');
    }
  }, [isOnAnnualSubscription, term]);

  const hasActiveSubscription = currentPlan !== null;
  const isCoveredByTeammate = computeIsCoveredByTeammate(
    workspaceSubscriptionStatus,
    currentUser()?.uid,
  );
  // True when THIS workspace's own coverage is active and non-Paddle (RevenueCat, purchased via
  // the mobile app) — either this workspace's own Organization (coveringUserID null) or this same
  // viewer's own personal subscription reaching it via the fallback path (coveringUserID === self,
  // excluded from isCoveredByTeammate above since that's for a fellow member's subscription, not
  // the viewer's own). billingUsage/currentPlan never reflect this — they're Paddle-only.
  const isOwnMobileSubscription =
    !isCoveredByTeammate &&
    workspaceSubscriptionStatus?.active === true &&
    workspaceSubscriptionStatus.subscriptionSource !== null &&
    workspaceSubscriptionStatus.subscriptionSource !== 'paddle';
  // Users already paying for an individual plan via Paddle upgrade their
  // existing subscription in place instead of opening a second checkout.
  const currentIndividualPlan = individualPlanFromPriceID(billingUsage?.activePriceID);
  // A subscription scheduled to cancel is still a live subscription in Paddle —
  // it keeps its plan until the period ends. Treat it as the customer's active
  // plan (not gone) so they resume it in place; starting a fresh checkout would
  // create a second subscription and double-bill them.
  const isLapsing = billingUsage?.isLapsing ?? false;
  const hasPaddleSubscription = hasActiveSubscription || currentIndividualPlan !== null;
  // The tabbed subscribed view (StatStrip/ManageSubscription/Billing tab) applies to any active
  // Paddle subscription — Team or individual (Starter/Professional) — not just Team. The paywall
  // is reserved for a genuinely brand-new customer with no Paddle involvement at all.
  const showSubscribedView = hasPaddleSubscription || isLapsing;
  // Only a brand-new customer (no Paddle subscription at all, and never one before —
  // hasEverSubscribed prevents trial abuse via cancel-then-resubscribe) is offered a free
  // trial. Existing subscribers change in place; a lapsing customer resumes
  // their existing subscription rather than re-purchasing.
  const offerTrial =
    !hasActiveSubscription &&
    currentIndividualPlan === null &&
    !isLapsing &&
    !billingUsage?.hasEverSubscribed;
  const isTrialing = billingUsage?.isTrialing ?? false;
  // In-place plan/seat changes are blocked while a cancellation is scheduled.
  // Trial subscriptions CAN upgrade or add seats — the backend uses Paddle's
  // `nextBilledAt` trick to preserve the trial-end billing date (EZ-5085).
  let changesLockedMessage: string | null = null;
  if (isLapsing) {
    changesLockedMessage = getTranslation(
      'Your subscription is scheduled to cancel. Resume it below to keep your current plan.',
    );
  }
  const changesLocked = changesLockedMessage !== null;
  const seatModalMode: 'additional' | 'total' = hasActiveSubscription ? 'additional' : 'total';
  let minSeats: number;
  if (hasActiveSubscription) {
    minSeats = Math.max(activeMembers, currentAllocation, 1);
  } else {
    minSeats = Math.max(TEAM_MIN_SEATS, activeMembers);
  }

  // WorkspaceSwitcher changes workspace by mutating the store — no navigate, no remount — so
  // these getters can be in flight when the workspace underneath them changes. Each one captures
  // the workspace it was issued for and drops its response if the user has since switched, so
  // workspace A's billing can never land in workspace B's view.
  const activeWorkspaceIDRef = React.useRef(currentWorkspace?.key);
  activeWorkspaceIDRef.current = currentWorkspace?.key;

  async function getBillingInfo({ keepStaleOnError = false }: { keepStaleOnError?: boolean } = {}) {
    const workspaceID = currentWorkspace?.key;
    try {
      const billingInfoResp = await getPaddleSubscriptionBillingInfoFunction()({
        workspaceID,
      });
      if (activeWorkspaceIDRef.current !== workspaceID) {
        return;
      }
      setBillingInfo(billingInfoResp.data);
      setBillingInfoError(false);
    } catch (error) {
      console.error('[Billing] Failed to load subscription info:', error);
      if (activeWorkspaceIDRef.current !== workspaceID) {
        return;
      }
      setBillingInfoError(true);
      if (!keepStaleOnError) {
        setBillingInfo(null);
      }
    }
  }

  async function getBillingUsage(): Promise<PaddleBillingUsage | null> {
    const workspaceID = currentWorkspace?.key;
    if (!workspaceID) {
      return null;
    }
    try {
      const usageResp = await getPaddleBillingFunction()({ workspaceID });
      if (activeWorkspaceIDRef.current !== workspaceID) {
        return null;
      }
      setBillingUsage(usageResp.data);
      setBillingUsageError(false);
      return usageResp.data;
    } catch (error) {
      console.error('[Billing] Failed to load billing usage:', error);
      if (activeWorkspaceIDRef.current !== workspaceID) {
        return null;
      }
      setBillingUsage(null);
      setBillingUsageError(true);
      return null;
    }
  }

  async function getWorkspaceCoverage(): Promise<WorkspaceSubscriptionStatus | undefined> {
    const workspaceID = currentWorkspace?.key;
    if (!workspaceID) {
      return undefined;
    }
    try {
      const response = await getWorkspaceSubscriptionStatusFunction()({ workspaceID });
      if (activeWorkspaceIDRef.current !== workspaceID) {
        return undefined;
      }
      setWorkspaceSubscriptionStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('[Billing] Failed to load workspace coverage:', error);
      // Deliberately no error state here: on failure, isCoveredByTeammate stays false and
      // rendering falls back to today's existing billingUsage/billingInfo-derived behavior,
      // unchanged (specs/004-client-subscription-integration research.md Decision 4).
      return undefined;
    }
  }

  // Every purchase now completes on a checkout route of its own, so this page is destroyed and
  // remounted rather than kept alive behind an overlay -- the return effect below is what tells
  // it a purchase just happened. The purchase itself is only recorded once Paddle's webhook
  // reaches our backend, which can lag the redirect by a few seconds, so this polls, refetching
  // until the new subscription is actually visible rather than refetching once and giving up
  // (EZ-5062).
  const CHECKOUT_REFRESH_MAX_ATTEMPTS = 8;
  const CHECKOUT_REFRESH_DELAY_MS = 2000;
  async function refetchUntilCheckoutReflected() {
    setBusyLabel(getTranslation('Finalizing your purchase...'));
    try {
      for (let attempt = 0; attempt < CHECKOUT_REFRESH_MAX_ATTEMPTS; attempt++) {
        const [, usage] = await Promise.all([getWorkspaceCoverage(), getBillingUsage()]);
        if (usage?.activePriceID != null) {
          await getBillingInfo();
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, CHECKOUT_REFRESH_DELAY_MS));
      }
      // Ran out of attempts -- one last refetch so the page reflects whatever the latest state
      // actually is, rather than silently giving up mid-poll.
      await Promise.all([getBillingInfo(), getWorkspaceCoverage(), getBillingUsage()]);
    } finally {
      setBusyLabel(null);
      notifyBillingChanged();
    }
  }
  // The return effect below must not re-run when this function's identity changes on every
  // render, so it reads it through a ref instead of depending on it.
  const refetchUntilCheckoutReflectedRef = React.useRef(refetchUntilCheckoutReflected);
  refetchUntilCheckoutReflectedRef.current = refetchUntilCheckoutReflected;

  /**
   * Every purchase completes on a checkout route, so Paddle redirects back here as a fresh page
   * load. Without this the buyer would land on a single mount fetch that beats the webhook and be
   * shown the paywall they just paid to leave. The marker is stripped so a refresh does not poll
   * again.
   */
  const checkoutReturnHandledRef = React.useRef(false);
  React.useEffect(() => {
    if (checkoutReturnHandledRef.current || !currentWorkspace?.key) {
      return;
    }
    if (searchParams.get(CHECKOUT_COMPLETE_PARAM) !== CHECKOUT_COMPLETE_VALUE) {
      return;
    }
    checkoutReturnHandledRef.current = true;
    const remaining = new URLSearchParams(searchParams);
    remaining.delete(CHECKOUT_COMPLETE_PARAM);
    setSearchParams(remaining, { replace: true });
    refetchUntilCheckoutReflectedRef.current();
  }, [searchParams, setSearchParams, currentWorkspace?.key]);

  React.useEffect(() => {
    // getWorkspaceSubscriptionStatus is the subscription-agnostic source of truth (resolves
    // Paddle or RevenueCat coverage alike) — resolve it first so we know whether this workspace
    // has any Paddle involvement at all before calling Paddle's own billing-info/usage functions.
    // Skip those only once coverage is POSITIVELY confirmed active AND non-Paddle (see
    // computeHasNoPaddleInvolvement, which is what "positively" costs): a still-loading, uncovered,
    // or unknown-source workspace still checks Paddle directly, since the viewer may hold their own
    // individual Paddle plan that isn't the winning subscription for workspace coverage (e.g. a
    // teammate's higher-tier RevenueCat plan outranks it).
    const workspaceID = currentWorkspace?.key;
    // The workspace store rehydrates asynchronously, so on a hard refresh of
    // /settings?tab=billing this effect first runs with no workspace at all. Staying in the
    // loading state is what makes that survivable: returning without it let both getters
    // early-return and flipped isLoading false, showing a paying customer the full paywall with
    // nothing that would ever retry.
    if (!workspaceID) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    async function loadBillingData() {
      const status = await getWorkspaceCoverage();
      if (computeHasNoPaddleInvolvement(status)) {
        setBillingInfo(null);
        setBillingUsage(null);
        return;
      }
      await Promise.allSettled([getBillingInfo(), getBillingUsage()]);
    }
    loadBillingData().finally(() => {
      if (!cancelled) {
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // Keyed on the workspace, not mount: WorkspaceSwitcher mutates the store in place, so
    // without this the page kept showing (and acting on) whichever workspace happened to be
    // current when it first mounted.
  }, [currentWorkspace?.key]);

  const [paymentRetryCount, setPaymentRetryCount] = React.useState(0);
  React.useEffect(() => {
    if (isLoading) {
      return;
    }
    const hasSubscription = (billingUsage?.purchasedSeats ?? 0) > 0;
    const cardMissing = !billingInfo?.paymentMethod?.card?.last4;
    if (!hasSubscription || !cardMissing) {
      return;
    }
    if (paymentRetryCount >= MAX_PAYMENT_DETAIL_RETRIES) {
      return;
    }
    const timer = setTimeout(() => {
      setPaymentRetryCount((previousCount) => previousCount + 1);
      getBillingInfo({ keepStaleOnError: true });
    }, 3000);
    return () => clearTimeout(timer);
  }, [
    isLoading,
    billingUsage?.purchasedSeats,
    billingInfo?.paymentMethod?.card?.last4,
    paymentRetryCount,
  ]);

  /**
   * Opens the add-seats picker when the upgrade modal's Add Seats sent the buyer here, once their
   * Team plan has loaded. The marker is stripped so a refresh does not reopen it. Without a Team
   * plan there is nothing to add seats to -- an individual plan's seats are bundled, and a store
   * plan has no web controls -- so the page is simply shown.
   */
  const addSeatsHandledRef = React.useRef(false);
  React.useEffect(() => {
    if (addSeatsHandledRef.current || isLoading) {
      return;
    }
    if (searchParams.get(ADD_SEATS_PARAM) !== '1') {
      return;
    }
    addSeatsHandledRef.current = true;
    const remaining = new URLSearchParams(searchParams);
    remaining.delete(ADD_SEATS_PARAM);
    setSearchParams(remaining, { replace: true });
    if (!currentPlan) {
      return;
    }
    if (isLapsing) {
      alert(
        getTranslation(
          'Your subscription is scheduled to cancel. Resume it first to change your seats.',
        ),
      );
      return;
    }
    openSeatSelection(currentPlan);
  }, [isLoading, searchParams, setSearchParams, currentPlan, isLapsing]);

  function priceIDFor(planCode: TeamPlanCode, opts: { withTrial: boolean }): string | undefined {
    // Teams Pro is deprecated for new purchases — seat changes for existing
    // Teams Pro subscribers reuse their current price so their plan and rate
    // never migrate as a side effect.
    if (planCode === 'TEAMS_PRO') {
      return billingUsage?.activePriceID ?? undefined;
    }
    const isProd = WebEnv.buildType === 'production';
    const priceMaps = {
      'monthly:prod': paddleTeamItemsNewMonthlyProd,
      'monthly:sandbox': paddleTeamItemsNewMonthlySandbox,
      'annual:prod': paddleTeamItemsNewAnnualProd,
      'annual:sandbox': paddleTeamItemsNewAnnualSandbox,
    } as const;
    const priceMapKey = `${term}:${isProd ? 'prod' : 'sandbox'}` as const;
    const variant = priceMaps[priceMapKey][planCode];
    return opts.withTrial ? variant.trial : variant.noTrial;
  }

  function openSeatSelection(planCode: TeamPlanCode) {
    if (!priceIDFor(planCode, { withTrial: offerTrial })) {
      alert(
        getTranslation(
          'Annual billing is not available yet — please contact support@easy-expense.com.',
        ),
      );
      return;
    }
    setPendingPlan(planCode);
  }

  /**
   * Hands a brand-new Team purchase to the Team Checkout page, which resolves the price from the
   * buyer's own billing state rather than being handed one. `priceId` is deliberately not passed:
   * the page decides Trial Eligibility itself, so a returning subscriber cannot arrive holding a
   * trial price. Existing subscribers never reach here -- they change in place.
   */
  function startTeamCheckout(seats: number) {
    const user = currentUser();
    if (!user) {
      return;
    }
    Analytics.track('team_checkout_opened', {
      term,
      seats,
      entry_point: 'web_billing',
    });
    const query = buildTeamCheckoutQuery({
      userID: user.uid,
      seats,
      term,
      email: user.email ?? '',
      source: 'web_billing',
      workspaceID: currentWorkspace?.key,
    });
    navigate(`${TEAM_CHECKOUT_PATH}?${query}`);
  }

  /**
   * Hands a brand-new individual purchase to the Individual Checkout page, which resolves the
   * price from the buyer's own billing state rather than being handed one -- so a returning
   * subscriber cannot arrive holding a trial price. Existing subscribers never reach here; they
   * change plan in place.
   */
  function startIndividualCheckout(planCode: IndividualPlanCode) {
    const user = currentUser();
    if (!user) {
      return;
    }
    Analytics.track('individual_checkout_opened', {
      plan: planCode,
      term,
      entry_point: 'web_billing',
    });
    const query = buildIndividualCheckoutQuery({
      userID: user.uid,
      plan: planCode,
      term,
      email: user.email ?? '',
      source: 'web_billing',
      workspaceID: currentWorkspace?.key,
    });
    navigate(`${INDIVIDUAL_CHECKOUT_PATH}?${query}`);
  }

  // A seat-count change that stays on the same plan tier and billing cycle is a
  // quantity-only update, not a price change. Mid-trial we keep the customer on
  // their current (trial) price so Paddle sees only a quantity change — it
  // rejects genuine price changes (tier / billing-cycle swap, or the
  // trial → no-trial migration) during a trial. Both the apply and preview
  // paths resolve the price through this so the previewed amount matches.
  const effectivePriceIDFor = (priceId: string): string => {
    const activePriceID = billingUsage?.activePriceID;
    const isQuantityOnly =
      !!activePriceID &&
      samePlanTier(priceId, activePriceID) &&
      (billingUsage?.frequency === 'year') === priceIsAnnual(priceId);
    if (billingUsage?.isTrialing && isQuantityOnly && activePriceID) {
      return activePriceID;
    }
    return priceId;
  };

  // Higher rank = higher tier. Downgrades are blocked during trial (EZ-5085).
  const INDIVIDUAL_PLAN_RANK: Record<IndividualPlanCode, number> = { STARTER: 1, PROFESSIONAL: 2 };

  /** True when the new price represents a lower individual plan tier while trialing. */
  const isTrialDowngrade = (priceId: string): boolean => {
    if (!isTrialing || !billingUsage?.activePriceID) {
      return false;
    }
    const newPlan = individualPlanFromPriceID(priceId);
    const activePlan = individualPlanFromPriceID(billingUsage.activePriceID);
    if (newPlan === null || activePlan === null) {
      return false;
    }
    return INDIVIDUAL_PLAN_RANK[newPlan] < INDIVIDUAL_PLAN_RANK[activePlan];
  };

  const alertTrialDowngrade = () => {
    alert(getTranslation("You can't downgrade your plan during a trial. Upgrades are allowed."));
  };

  // Runs a billing mutation behind a busy label, refreshes billing state on
  // success, and surfaces a single support-pointing alert on failure. Shared by
  // the cancel / resume / plan-change handlers so their busy-label and error
  // handling stay identical. getBillingInfo/getBillingUsage/getWorkspaceCoverage
  // swallow their own errors, so the post-mutation refetch never turns a
  // success into a failure. getWorkspaceCoverage is included so the seats
  // allowance shown in "Seats Used" (and any teammate/mobile coverage view)
  // updates immediately instead of only on the next full page load (EZ-5077).
  const runBillingAction = async (
    busyLabel: string,
    errorMessage: string,
    mutate: () => Promise<unknown>,
  ) => {
    setBusyLabel(busyLabel);
    try {
      await mutate();
      await Promise.all([getBillingInfo(), getBillingUsage(), getWorkspaceCoverage()]);
      notifyBillingChanged();
    } catch (error) {
      console.error('[Billing] Action failed:', error);
      alert(errorMessage);
    } finally {
      setBusyLabel(null);
    }
  };

  async function applySubscriptionChange(priceId: string, seats: number) {
    if (isTrialDowngrade(priceId)) {
      alertTrialDowngrade();
      return;
    }
    // Paddle rejects in-place changes on a scheduled-to-cancel subscription, so
    // guard here too — the ladder CTAs are disabled while lapsing, but this is
    // the chokepoint every change path funnels through.
    if (isLapsing) {
      alert(
        getTranslation(
          'Your subscription is scheduled to cancel. Resume it before making changes.',
        ),
      );
      return;
    }
    await runBillingAction(
      getTranslation('Updating your subscription...'),
      getTranslation(
        'We could not complete the change. Please try again or contact support@easy-expense.com.',
      ),
      () =>
        upgradePaddleSubscriptionFunction()({
          newPriceID: effectivePriceIDFor(priceId),
          quantity: seats,
          workspaceID: currentWorkspace?.key,
        }),
    );
  }

  const isProdBuild = WebEnv.buildType === 'production';
  const individualItems = isProdBuild ? paddleIndividualItemsProd : paddleIndividualItemsSandbox;

  function purchaseIndividualPlan(planCode: IndividualPlanCode) {
    const variants = individualItems[planCode][term];
    if (hasPaddleSubscription) {
      if (isTrialDowngrade(variants.noTrial)) {
        alertTrialDowngrade();
        return;
      }
      // In-place change bills a proration immediately with no checkout step of
      // its own, so confirm before applying it.
      const isTierUpgradeDuringTrial = isTrialing && !!billingUsage?.activePriceID;
      setConfirmModalProps({
        title: getTranslation('Change your plan'),
        message: isTierUpgradeDuringTrial
          ? getTranslation('Your plan changes now, and your trial continues as normal.')
          : getTranslation('This takes effect as soon as you confirm.'),
        confirmLabel: getTranslation('Continue'),
        getPreview: () => previewChange(variants.noTrial, 1),
        onConfirm: () => applySubscriptionChange(variants.noTrial, 1),
      });
    } else {
      startIndividualCheckout(planCode);
    }
  }

  // Use .at(-1) so subsequent reads aren't destructive — .pop() mutates
  // billingInfo.portalSession.urls.subscriptions and breaks the second tap.
  // Derived before the paywall return so individual-plan subscribers (who take
  // that branch, since hasActiveSubscription is team-only) can still cancel and
  // manage payment.
  const lastSubscription = billingInfo?.portalSession?.urls.subscriptions.at(-1);
  const updatePaymentMethodUrl = lastSubscription?.updateSubscriptionPaymentMethod;

  // Cancel through our backend with effectiveFrom 'next_billing_period' rather
  // than the hosted portal, so a trial always cancels at trial end (the
  // subscription stays active and reports isLapsing) instead of immediately.
  const performCancelSubscription = () =>
    runBillingAction(
      getTranslation('Canceling your subscription...'),
      getTranslation(
        'We could not cancel your subscription. Please try again or contact support@easy-expense.com.',
      ),
      () => cancelPaddleSubscriptionFunction()({ workspaceID: currentWorkspace?.key }),
    );

  const onCancelSubscription = () => {
    setConfirmModalProps({
      title: getTranslation('Cancel subscription'),
      message: getTranslation(
        'You will retain access until the end of the current billing period.',
      ),
      dismissLabel: getTranslation('Keep subscription'),
      confirmLabel: getTranslation('Cancel subscription'),
      destructive: true,
      onConfirm: performCancelSubscription,
    });
  };

  // Reactivate a subscription that is scheduled to cancel by clearing the
  // scheduled change in Paddle. Keeps the existing plan, price, and billing
  // date — no new checkout, so the customer is never double-billed.
  const performResumeSubscription = () =>
    runBillingAction(
      getTranslation('Resuming your subscription...'),
      getTranslation(
        'We could not resume your subscription. Please try again or contact support@easy-expense.com.',
      ),
      () => resumePaddleSubscriptionFunction()({ workspaceID: currentWorkspace?.key }),
    );

  const onResumeSubscription = () => {
    setConfirmModalProps({
      title: getTranslation('Resume subscription'),
      message: getTranslation('Your plan, price, and billing date stay the same.'),
      confirmLabel: getTranslation('Resume subscription'),
      onConfirm: performResumeSubscription,
    });
  };

  const previewChange = async (
    priceId: string,
    seats: number,
  ): Promise<SubscriptionChargePreview> => {
    const response = await previewPaddleSubscriptionChangeFunction()({
      newPriceID: effectivePriceIDFor(priceId),
      quantity: seats,
      workspaceID: currentWorkspace?.key,
    });
    return response.data;
  };

  const confirmModal = confirmModalProps ? (
    <ConfirmationModal
      showModal
      setShowModal={(show) => {
        if (!show) {
          setConfirmModalProps(null);
        }
      }}
      title={confirmModalProps.title}
      message={confirmModalProps.message}
      dismissLabel={confirmModalProps.dismissLabel}
      confirmLabel={confirmModalProps.confirmLabel}
      destructive={confirmModalProps.destructive}
      onConfirm={confirmModalProps.onConfirm}
      getPreview={confirmModalProps.getPreview}
    />
  ) : null;

  const onUpdatePaymentMethod = () => {
    if (!updatePaymentMethodUrl) {
      return;
    }
    window.open(updatePaymentMethodUrl, '_blank')?.focus();
  };

  const historyUrl = paymentsHistoryURL(billingInfo?.portalSession?.urls.general.overview);

  const onViewHistory = () => {
    if (!historyUrl) {
      return;
    }
    window.open(historyUrl, '_blank')?.focus();
  };

  const addSeatsPrefill = Math.max(overSeatsCount, 1);
  const pendingPlanMonthlyPrice =
    pendingPlan === 'TEAMS_PRO' ? LEGACY_TEAMS_PRO_MONTHLY_PRICE : MONTHLY_PRICES.TEAMS_BASIC;

  const seatModal = pendingPlan ? (
    <SeatSelectionModal
      showModal={pendingPlan !== null}
      setShowModal={(show) => {
        if (!show) {
          setPendingPlan(null);
        }
      }}
      planTitle={getTeamPlanTitle(pendingPlan)}
      monthlyPricePerSeat={pendingPlanMonthlyPrice}
      minSeats={minSeats}
      term={term}
      isUpgrade={false}
      mode={seatModalMode}
      currentAllocation={currentAllocation}
      initialAdditionalSeats={addSeatsPrefill}
      onConfirm={(seats) => {
        const plan = pendingPlan;
        setPendingPlan(null);
        if (!plan) {
          return;
        }
        const priceId = priceIDFor(plan, { withTrial: offerTrial });
        if (!priceId) {
          alert(
            getTranslation(
              'Annual billing is not available yet — please contact support@easy-expense.com.',
            ),
          );
          return;
        }
        if (hasPaddleSubscription) {
          if (isTrialDowngrade(priceId)) {
            alertTrialDowngrade();
            return;
          }
          // An in-place change bills a proration immediately with no checkout
          // step, so confirm before applying it.
          // Compare against activePriceID (not priceId) so that the trial→noTrial
          // price difference doesn't mis-classify a seat-only change as a tier change.
          const resolvedPriceId = effectivePriceIDFor(priceId);
          const isTierChangeDuringTrial =
            isTrialing &&
            !!billingUsage?.activePriceID &&
            resolvedPriceId !== billingUsage.activePriceID;
          let confirmMessage: string;
          if (isTierChangeDuringTrial) {
            confirmMessage = getTranslation(
              'Your plan changes now, and your trial continues as normal.',
            );
          } else if (isTrialing) {
            confirmMessage = getTranslation(
              'Your seats change now, and your trial continues as normal.',
            );
          } else {
            confirmMessage = getTranslation('This takes effect as soon as you confirm.');
          }
          setConfirmModalProps({
            title: getTranslation('Update your subscription'),
            message: confirmMessage,
            confirmLabel: getTranslation('Continue'),
            getPreview: () => previewChange(priceId, seats),
            onConfirm: () => applySubscriptionChange(priceId, seats),
          });
        } else {
          startTeamCheckout(seats);
        }
      }}
    />
  ) : null;

  if (isLoading) {
    return (
      <Layout.Column center grow>
        <LoadingSpinner />
        <Spacer.Vertical size={16} />
        <OpenSans.Primary size="s-16">{getTranslation('Loading...')}</OpenSans.Primary>
      </Layout.Column>
    );
  }

  if (busyLabel) {
    return (
      <Layout.Column center grow>
        <LoadingSpinner />
        <Spacer.Vertical size={16} />
        <OpenSans.Primary size="s-16">{busyLabel}</OpenSans.Primary>
      </Layout.Column>
    );
  }

  if ((billingInfoError && !billingInfo) || (billingUsageError && !billingUsage)) {
    return (
      <Layout.Column py={32}>
        <Layout.Column
          bg="grayXXLight"
          radius={8}
          border={[1, 'solid', 'destructive']}
          py={24}
          px={24}
          align="center"
        >
          <Icon name="alert-circle-outline" size={32} color={theme.colors.destructive} />
          <Spacer.Vertical size={8} />
          <OpenSans.Primary size="s-16" weight="bold-700" color="destructive" center>
            {getTranslation("We couldn't load your billing details.")}
          </OpenSans.Primary>
          <Spacer.Vertical size={4} />
          <OpenSans.Secondary size="s-16" center>
            {getTranslation('Please reload the page to try again.')}
          </OpenSans.Secondary>
          <Spacer.Vertical size={16} />
          <Button.Primary
            content={getTranslation('Reload page')}
            onClick={() => window.location.reload()}
          />
        </Layout.Column>
      </Layout.Column>
    );
  }

  if (isCoveredByTeammate) {
    // A fellow member's own subscription covers this workspace — show the same plan/seats/billing
    // details a direct subscriber would see (SubscriptionTab in readOnly mode), but never any of
    // the manage/cancel/upgrade/payment-method controls, since none of them belong to a
    // subscription this viewer doesn't hold (FR-003). Never reachable for the covering member
    // themselves — they see the normal management view below instead.
    const coveringMemberName = members?.find(
      (member) => member.userID === workspaceSubscriptionStatus?.coveringUserID,
    )?.displayName;
    return (
      <Layout.Column py={32} style={{ maxWidth: 1100 }}>
        <Layout.Column bg="grayXXLight" radius={8} py={24} px={24} align="center">
          <Icon name="checkmark-circle-outline" size={32} color={theme.colors.success} />
          <Spacer.Vertical size={8} />
          <OpenSans.Primary size="s-16" weight="bold-700" center>
            {getTranslation("This workspace is covered by a teammate's subscription")}
          </OpenSans.Primary>
          <Spacer.Vertical size={4} />
          <OpenSans.Secondary size="s-16" center>
            {coveringMemberName
              ? `${coveringMemberName} ${getTranslation('manages the subscription for this workspace.')}`
              : getTranslation('A teammate manages the subscription for this workspace.')}
          </OpenSans.Secondary>
        </Layout.Column>
        <Spacer.Vertical size={24} />
        <SubscriptionTab
          billingUsage={billingUsage}
          currentPlan={currentPlan}
          // billingUsage.activeSeats/purchasedSeats are scoped to this workspace's own
          // Organization — wrong here, since the covering subscription's seat usage is the
          // total unique members across every workspace it covers (getWorkspaceSubscriptionStatus
          // already computes that correctly; fall back to the org-scoped numbers only if that
          // resolution hasn't loaded).
          activeMembers={workspaceSubscriptionStatus?.seatsUsed ?? activeMembers}
          seatsAllowed={workspaceSubscriptionStatus?.seatsAllowed ?? currentAllocation}
          isOverSeats={isOverSeats}
          term={term}
          setTerm={setTerm}
          onAddSeats={() => {}}
          disableMonthlyTerm={isOnAnnualSubscription}
          readOnly
          statDisplayOverride={resolveWorkspaceStatusDisplay(workspaceSubscriptionStatus)}
        />
      </Layout.Column>
    );
  }

  if (isOwnMobileSubscription) {
    // This workspace's coverage is active via RevenueCat (purchased on iOS/Android), not Paddle —
    // billingUsage/currentPlan never reflect this, so show the plan/tier and seat usage from
    // workspaceSubscriptionStatus directly instead of falling through to the "Choose a plan"
    // paywall. No Paddle management controls apply; the subscription can only be managed through
    // the App Store / Play Store (see PlatformSubscriptionNote for that guidance elsewhere).
    const display = resolveWorkspaceStatusDisplay(workspaceSubscriptionStatus);
    return (
      <Layout.Column py={32} style={{ maxWidth: 1100 }}>
        <Layout.Column bg="grayXXLight" radius={8} py={24} px={24} align="center">
          <Icon name="checkmark-circle-outline" size={32} color={theme.colors.success} />
          <Spacer.Vertical size={8} />
          <OpenSans.Primary size="s-16" weight="bold-700" center>
            {getTranslation('Your subscription is managed on iOS or Android')}
          </OpenSans.Primary>
          <Spacer.Vertical size={4} />
          <OpenSans.Secondary size="s-16" center>
            {getTranslation(
              'Manage or cancel your plan through the Apple App Store or Google Play. To upgrade to a team plan, please contact support.',
            )}
          </OpenSans.Secondary>
        </Layout.Column>
        <Spacer.Vertical size={24} />
        <StatStrip
          seatsUsed={workspaceSubscriptionStatus?.seatsUsed ?? activeMembers}
          seatsAllowed={workspaceSubscriptionStatus?.seatsAllowed ?? currentAllocation}
          planLabel={display.planLabel}
          nextBillingLabel={display.nextBillingLabel}
          perPeriodLabel={display.perPeriodLabel}
          perPeriodAmount={display.perPeriodAmount}
        />
      </Layout.Column>
    );
  }

  if (!showSubscribedView) {
    // Reachable by anyone with no currently-active Paddle subscription, including a
    // returning customer who canceled before -- offerTrial already accounts for that
    // (hasEverSubscribed), so the Team CTA must too instead of always pitching a trial.
    const teamCTA = offerTrial ? getTranslation('Start Team Trial') : getTranslation('Subscribe');

    return (
      <Layout.Column grow>
        <Layout.Column py={32} style={{ maxWidth: 1100 }}>
          <OpenSans.Primary
            weight="bold-700"
            size="m-18"
            color="primary"
            role="heading"
            aria-level={2}
          >
            {getTranslation('Choose a plan')}
          </OpenSans.Primary>
          <Spacer.Vertical size={4} />
          <OpenSans.Primary size="s-16" color="secondary">
            {getTranslation('Choose a plan and select how many seats you need to get started.')}
          </OpenSans.Primary>
          <Spacer.Vertical size={16} />
          {/* Show the platform-subscription note here too so users
              already paying on iOS/Android (currentPlan === null) see
              the warning before signing up for a second sub. */}
          <PlatformSubscriptionNote />
          <Spacer.Vertical size={16} />
          <Layout.Row style={{ justifyContent: 'center' }}>
            <TermToggle
              term={term}
              setTerm={setTerm}
              disableMonthly={isOnAnnualSubscription}
              savingsLabel={getTranslation('Save up to 50%')}
            />
          </Layout.Row>
          {isOnAnnualSubscription ? (
            <>
              <Spacer.Vertical size={6} />
              <Layout.Row style={{ justifyContent: 'center' }}>
                <OpenSans.Primary size="xs-12" color="secondary" center>
                  {getTranslation(
                    "You're on an annual plan. Contact support@easy-expense.com to switch billing cycles.",
                  )}
                </OpenSans.Primary>
              </Layout.Row>
            </>
          ) : null}
          <Spacer.Vertical size={8} />
          <Layout.Row style={{ justifyContent: 'center' }}>
            <OpenSans.Primary size="xs-12" color="secondary">
              {getTranslation('All plans in USD. Excludes tax.')}
            </OpenSans.Primary>
          </Layout.Row>
          {changesLockedMessage ? (
            <>
              <Spacer.Vertical size={12} />
              <Layout.Row style={{ justifyContent: 'center' }}>
                <OpenSans.Primary size="xs-12" color="secondary" center>
                  {changesLockedMessage}
                </OpenSans.Primary>
              </Layout.Row>
            </>
          ) : null}
          <Spacer.Vertical size={24} />
          <PlanGrid
            term={term}
            offerTrial={offerTrial}
            changesLocked={changesLocked}
            currentIndividualPlan={currentIndividualPlan}
            isOnAnnualSubscription={isOnAnnualSubscription}
            hasTeamSubscription={hasActiveSubscription}
            seatsUsed={activeMembers}
            onPressIndividualPlan={purchaseIndividualPlan}
            teamCTA={teamCTA}
            teamCtaDisabled={changesLocked}
            onPressTeam={() => openSeatSelection('TEAMS_BASIC')}
          />
          <Spacer.Vertical size={24} />
          <TrustStrip />
          {seatModal}
          {confirmModal}
        </Layout.Column>
      </Layout.Column>
    );
  }

  const handleAddSeats = () => {
    // An in-place seat change fails on a subscription scheduled to cancel, so
    // don't route a lapsing team subscriber into a dead-end preview/apply.
    if (isLapsing) {
      alert(
        getTranslation(
          'Your subscription is scheduled to cancel. Resume it first to change your seats.',
        ),
      );
      return;
    }
    if (currentPlan) {
      openSeatSelection(currentPlan);
    }
  };

  // Switches the active Team subscription from monthly to annual at the same seat count —
  // a term-only change, so it skips the add-seats modal (which always adds at least one
  // seat) and applies directly, same as any other in-place plan change.
  const handleSwitchToAnnual = () => {
    if (isLapsing) {
      alert(
        getTranslation(
          'Your subscription is scheduled to cancel. Resume it first to change your billing cycle.',
        ),
      );
      return;
    }
    if (!currentPlan) {
      return;
    }
    const priceId = priceIDFor(currentPlan, { withTrial: offerTrial });
    if (!priceId) {
      alert(
        getTranslation(
          'Annual billing is not available yet — please contact support@easy-expense.com.',
        ),
      );
      return;
    }
    // Monthly → annual is always an upgrade direction; allowed during trial.
    setConfirmModalProps({
      title: getTranslation('Switch to annual billing'),
      message: isTrialing
        ? getTranslation('Your billing cycle changes now, and your trial continues as normal.')
        : getTranslation('This takes effect as soon as you confirm.'),
      confirmLabel: getTranslation('Continue'),
      getPreview: () => previewChange(priceId, currentAllocation),
      onConfirm: () => applySubscriptionChange(priceId, currentAllocation),
    });
  };

  // Toggling to annual at the same seat count is a term-only change (handleSwitchToAnnual),
  // not a seat purchase — the Team card's CTA and handler reflect that instead of dead-ending
  // in the add-seats flow (which always adds at least one seat).
  const isSwitchingToAnnualPlan = term === 'annual' && !isOnAnnualSubscription;
  const scrollToPlanGrid = () => {
    planGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // This branch (showSubscribedView) also covers an individual (Starter/Professional)
  // subscriber, for whom the Team card isn't "their plan" to add seats to — it's an upgrade,
  // same as the no-subscription paywall's Team card (openSeatSelection in 'total' mode).
  let teamCardCTA = getTranslation('Upgrade');
  let teamCardCtaDisabled = changesLocked;
  let onPressTeamCard = () => openSeatSelection('TEAMS_BASIC');
  if (hasActiveSubscription) {
    teamCardCTA = isSwitchingToAnnualPlan
      ? getTranslation('Switch to annual billing')
      : getTranslation('Add seats');
    teamCardCtaDisabled = isLapsing;
    onPressTeamCard = isSwitchingToAnnualPlan ? handleSwitchToAnnual : handleAddSeats;
  }
  // Team only. An individual (Starter/Professional) subscriber's seat allowance is bundled with
  // the plan and can't be topped up, so they get no "Add seats" row and the over-seats banner
  // points them at "Change plan" instead -- see SubscriptionTab (EZ-5172).
  const addSeatsHandler = hasActiveSubscription ? handleAddSeats : undefined;

  const planGrid = (
    <PlanGrid
      term={term}
      offerTrial={offerTrial}
      changesLocked={changesLocked}
      currentIndividualPlan={currentIndividualPlan}
      isOnAnnualSubscription={isOnAnnualSubscription}
      hasTeamSubscription={hasActiveSubscription}
      seatsUsed={activeMembers}
      onPressIndividualPlan={purchaseIndividualPlan}
      teamCTA={teamCardCTA}
      teamCtaDisabled={teamCardCtaDisabled}
      onPressTeam={onPressTeamCard}
    />
  );

  return (
    <Layout.Column grow>
      <Layout.Column py={32} style={{ maxWidth: 1100 }}>
        <Layout.Row style={{ borderBottom: `1px solid ${theme.colors.inputBorder}` }}>
          <TabButton
            label={getTranslation('Subscription')}
            active={activeTab === 'subscription'}
            onClick={() => setActiveTab('subscription')}
            badge={isOverSeats ? overSeatsCount : 0}
          />
          <TabButton
            label={getTranslation('Billing')}
            active={activeTab === 'billing'}
            onClick={() => setActiveTab('billing')}
          />
        </Layout.Row>
        <Spacer.Vertical size={24} />

        {activeTab === 'subscription' ? (
          <SubscriptionTab
            billingUsage={billingUsage}
            currentPlan={currentPlan}
            // Same subscription-wide seat usage shown to a covered teammate (see the
            // isCoveredByTeammate branch above) — the owner's own view must agree with it rather
            // than showing this workspace's own Organization membership count.
            activeMembers={workspaceSubscriptionStatus?.seatsUsed ?? activeMembers}
            seatsAllowed={workspaceSubscriptionStatus?.seatsAllowed ?? currentAllocation}
            isOverSeats={isOverSeats}
            term={term}
            setTerm={setTerm}
            onAddSeats={addSeatsHandler}
            addSeatsDisabled={isLapsing}
            onCancel={isLapsing ? undefined : onCancelSubscription}
            onResume={isLapsing ? onResumeSubscription : undefined}
            onChangePlan={scrollToPlanGrid}
            planGrid={planGrid}
            planGridRef={planGridRef}
            disableMonthlyTerm={isOnAnnualSubscription}
          />
        ) : null}

        {activeTab === 'billing' && billingInfo ? (
          <BillingTab
            billingInfo={billingInfo}
            onUpdatePaymentMethod={onUpdatePaymentMethod}
            onViewHistory={onViewHistory}
          />
        ) : null}

        {seatModal}
        {confirmModal}
      </Layout.Column>
    </Layout.Column>
  );
};
